import React, { useEffect, useState } from 'react';
import { usersAPI, sitesAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import { 
    Users, 
    Plus, 
    Pencil, 
    Trash2, 
    Search,
    Shield,
    ShieldCheck,
    ShieldAlert,
    Eye,
    EyeOff,
    KeyRound,
    UserCheck,
    UserX,
    MapPin,
    Building2,
    Check,
    BookOpen,
    Truck,
    History,
    Smartphone,
    Calculator
} from 'lucide-react';
import RolePermissionsPage from '@/pages/RolePermissionsPage';
import LoginLogsTab from '@/pages/users/LoginLogsTab';
import DevicesTab from '@/pages/users/DevicesTab';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from '@/context/AuthContext';

const ROLES = [
    { value: 'admin', label: 'Administrateur', description: 'Accès complet à toutes les fonctionnalités', icon: ShieldCheck, color: 'bg-purple-100 text-purple-700' },
    { value: 'supervisor', label: 'Superviseur', description: 'Gestion étendue sans administration des utilisateurs', icon: ShieldCheck, color: 'bg-indigo-100 text-indigo-700' },
    { value: 'manager', label: 'Manager', description: 'Gestion des commandes et inventaires', icon: Shield, color: 'bg-blue-100 text-blue-700' },
    { value: 'chef_cuisine', label: 'Chef de Cuisine', description: 'Recettes, produits et pertes — droits à la carte', icon: Shield, color: 'bg-orange-100 text-orange-700' },
    { value: 'livreur', label: 'Livreur', description: 'Réceptions et suivi des livraisons', icon: Truck, color: 'bg-amber-100 text-amber-700' },
    { value: 'operator', label: 'Opérateur', description: 'Saisie des réceptions et inventaires', icon: ShieldAlert, color: 'bg-emerald-100 text-emerald-700' },
    { value: 'facturation', label: 'Facturation', description: 'Lecture seule + Production Concept Lab', icon: Calculator, color: 'bg-teal-100 text-teal-700' },
    { value: 'production', label: 'Production', description: 'Page Production + impression des étiquettes', icon: Shield, color: 'bg-cyan-100 text-cyan-700' },
    { value: 'comptabilite', label: 'Comptabilité', description: 'Consultation financière — droits à affiner', icon: Calculator, color: 'bg-rose-100 text-rose-700' },
    { value: 'readonly', label: 'Lecture seule', description: 'Consultation uniquement', icon: Eye, color: 'bg-slate-100 text-slate-700' },
];

export default function UsersPage() {
    const { user: currentUser, hasPermission } = useAuth();
    const canViewLogins = hasPermission('users_logins_view');
    const [users, setUsers] = useState([]);
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState('all');
    
    // Dialog states
    const [createDialog, setCreateDialog] = useState(false);
    const [editDialog, setEditDialog] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [passwordDialog, setPasswordDialog] = useState(false);
    const [showResetPassword, setShowResetPassword] = useState(false);
    
    const [selectedUser, setSelectedUser] = useState(null);
    const [userForm, setUserForm] = useState({
        name: '',
        username: '',
        email: '',
        password: '',
        role: 'operator',
        guide_only: false,
        allowed_sites: [],
        allSitesAccess: true,
        guide_sites: [],
    });
    const [newPassword, setNewPassword] = useState('');
    
    // Onglet actif
    const [activeTab, setActiveTab] = useState('users');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [usersRes, sitesRes] = await Promise.all([
                usersAPI.getAll(),
                sitesAPI.getAll()
            ]);
            setUsers(usersRes.data);
            setSites(sitesRes.data);
        } catch (error) {
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async () => {
        if (!userForm.name || !userForm.username || !userForm.password) {
            toast.error('Veuillez remplir tous les champs obligatoires');
            return;
        }
        
        try {
            const payload = {
                name: userForm.name,
                username: userForm.username,
                email: userForm.email,
                password: userForm.password,
                role: userForm.role,
                guide_only: userForm.guide_only,
                allowed_sites: userForm.allSitesAccess ? null : userForm.allowed_sites,
                guide_sites: userForm.guide_sites.length > 0 ? userForm.guide_sites : null
            };
            await usersAPI.create(payload);
            toast.success('Utilisateur créé avec succès');
            setCreateDialog(false);
            resetForm();
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        }
    };

    const handleEditUser = (user) => {
        setSelectedUser(user);
        const hasAllAccess = !user.allowed_sites || user.allowed_sites.length === 0;
        setUserForm({
            name: user.name,
            username: user.username || '',
            email: user.email || '',
            password: '',
            role: user.role || 'admin',
            guide_only: user.guide_only || false,
            allowed_sites: user.allowed_sites || [],
            allSitesAccess: hasAllAccess,
            guide_sites: user.guide_sites || [],
        });
        setEditDialog(true);
    };

    const handleUpdateUser = async () => {
        if (!userForm.name) {
            toast.error('Le nom est obligatoire');
            return;
        }
        
        try {
            const payload = {
                name: userForm.name,
                username: userForm.username,
                email: userForm.email,
                role: userForm.role,
                guide_only: userForm.guide_only,
                allowed_sites: userForm.allSitesAccess ? [] : userForm.allowed_sites,
                guide_sites: userForm.guide_sites.length > 0 ? userForm.guide_sites : []
            };
            await usersAPI.update(selectedUser.id, payload);
            toast.success('Utilisateur mis à jour');
            setEditDialog(false);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        }
    };

    const handleToggleActive = async (user) => {
        try {
            await usersAPI.update(user.id, { is_active: !user.is_active });
            toast.success(user.is_active ? 'Utilisateur désactivé' : 'Utilisateur activé');
            loadData();
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        }
    };

    const handleDeleteUser = async () => {
        try {
            await usersAPI.delete(selectedUser.id);
            toast.success('Utilisateur supprimé');
            setDeleteDialog(false);
            loadData();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleResetPassword = async () => {
        if (!newPassword || newPassword.length < 8) {
            toast.error('Le mot de passe doit contenir au moins 8 caractères');
            return;
        }
        
        try {
            await usersAPI.resetPassword(selectedUser.id, newPassword);
            toast.success('Mot de passe réinitialisé');
            setPasswordDialog(false);
            setNewPassword('');
        } catch (error) {
            toast.error('Erreur lors de la réinitialisation');
        }
    };

    const resetForm = () => {
        setUserForm({ 
            name: '', 
            username: '', 
            email: '',
            password: '', 
            role: 'operator',
            guide_only: false,
            allowed_sites: [],
            allSitesAccess: true,
            guide_sites: [],
        });
    };

    const toggleSiteSelection = (siteId) => {
        setUserForm(prev => {
            const newSites = prev.allowed_sites.includes(siteId)
                ? prev.allowed_sites.filter(id => id !== siteId)
                : [...prev.allowed_sites, siteId];
            return { ...prev, allowed_sites: newSites };
        });
    };

    const toggleGuideSite = (siteId) => {
        setUserForm(prev => {
            const newSites = prev.guide_sites.includes(siteId)
                ? prev.guide_sites.filter(id => id !== siteId)
                : [...prev.guide_sites, siteId];
            return { ...prev, guide_sites: newSites };
        });
    };

    const getRoleInfo = (roleValue) => {
        return ROLES.find(r => r.value === roleValue) || ROLES[0];
    };

    const getSiteNames = (allowedSites) => {
        if (!allowedSites || allowedSites.length === 0) return null;
        return allowedSites.map(siteId => {
            const site = sites.find(s => s.id === siteId);
            return site?.name || 'Site inconnu';
        });
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch = 
            user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.username?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRole = filterRole === 'all' || user.role === filterRole;
        return matchesSearch && matchesRole;
    });

    // Site selection component for forms
    const SiteSelector = () => (
        <div className="space-y-3">
            <Label className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Sites autorisés
            </Label>
            
            <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg">
                <Checkbox 
                    id="all-sites"
                    checked={userForm.allSitesAccess}
                    onCheckedChange={(checked) => {
                        setUserForm(prev => ({ 
                            ...prev, 
                            allSitesAccess: checked,
                            allowed_sites: checked ? [] : prev.allowed_sites
                        }));
                    }}
                />
                <label htmlFor="all-sites" className="text-sm font-medium cursor-pointer">
                    Accès à tous les sites
                </label>
            </div>
            
            {!userForm.allSitesAccess && (
                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                    {sites.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-2">Aucun site disponible</p>
                    ) : (
                        sites.map(site => (
                            <div 
                                key={site.id} 
                                className={`flex items-center space-x-2 p-2 rounded-lg cursor-pointer transition-colors ${
                                    userForm.allowed_sites.includes(site.id) 
                                        ? 'bg-blue-50 border border-blue-200' 
                                        : 'hover:bg-slate-50'
                                }`}
                                onClick={() => toggleSiteSelection(site.id)}
                            >
                                <Checkbox 
                                    checked={userForm.allowed_sites.includes(site.id)}
                                    className="pointer-events-none"
                                />
                                <MapPin className="w-4 h-4 text-slate-400" />
                                <span className="text-sm">{site.name}</span>
                                {userForm.allowed_sites.includes(site.id) && (
                                    <Check className="w-4 h-4 text-blue-500 ml-auto" />
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
            
            {!userForm.allSitesAccess && userForm.allowed_sites.length > 0 && (
                <p className="text-xs text-slate-500">
                    {userForm.allowed_sites.length} site(s) sélectionné(s)
                </p>
            )}
            
            {!userForm.allSitesAccess && userForm.allowed_sites.length === 0 && (
                <p className="text-xs text-amber-600">
                    Selectionnez au moins un site
                </p>
            )}
        </div>
    );

    const GuideSiteSelector = () => (
        <div className="space-y-3">
            <Label className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-red-500" />
                Sites Guide du Sushiman
            </Label>
            <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                {sites.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-2">Aucun site disponible</p>
                ) : (
                    sites.map(site => (
                        <div 
                            key={site.id} 
                            className={`flex items-center space-x-2 p-2 rounded-lg cursor-pointer transition-colors ${
                                userForm.guide_sites.includes(site.id)
                                    ? 'bg-red-50 border border-red-200'
                                    : 'hover:bg-slate-50'
                            }`}
                            onClick={() => toggleGuideSite(site.id)}
                        >
                            <Checkbox
                                checked={userForm.guide_sites.includes(site.id)}
                                className="pointer-events-none"
                            />
                            <MapPin className="w-4 h-4 text-slate-400" />
                            <span className="text-sm">{site.name}</span>
                            {userForm.guide_sites.includes(site.id) && (
                                <Check className="w-4 h-4 text-red-500 ml-auto" />
                            )}
                        </div>
                    ))
                )}
            </div>
            {userForm.guide_sites.length > 0 && (
                <p className="text-xs text-slate-500">
                    {userForm.guide_sites.length} site(s) Guide selectionne(s)
                </p>
            )}
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">Utilisateurs</h1>
                    <p className="text-slate-600 mt-1">Gestion des comptes, des rôles et des sites autorisés</p>
                </div>
                {activeTab === 'users' && (
                <Button 
                    onClick={() => {
                        resetForm();
                        setCreateDialog(true);
                    }} 
                    className="bg-slate-900 hover:bg-slate-800"
                    data-testid="new-user-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Nouvel utilisateur
                </Button>
                )}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-slate-200">
                <button
                    onClick={() => setActiveTab('users')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'users'
                            ? 'border-slate-900 text-slate-900'
                            : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid="tab-users"
                >
                    <Users className="w-4 h-4 inline mr-1.5" />
                    Utilisateurs
                </button>
                <button
                    onClick={() => setActiveTab('profiles')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'profiles'
                            ? 'border-slate-900 text-slate-900'
                            : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid="tab-profiles"
                >
                    <KeyRound className="w-4 h-4 inline mr-1.5" />
                    Profils & Droits
                </button>
                {canViewLogins && (
                <button
                    onClick={() => setActiveTab('logins')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'logins'
                            ? 'border-slate-900 text-slate-900'
                            : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid="tab-logins"
                >
                    <History className="w-4 h-4 inline mr-1.5" />
                    Connexions
                </button>
                )}
                {currentUser?.role === 'admin' && (
                <button
                    onClick={() => setActiveTab('devices')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'devices'
                            ? 'border-slate-900 text-slate-900'
                            : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid="tab-devices"
                >
                    <Smartphone className="w-4 h-4 inline mr-1.5" />
                    Appareils
                </button>
                )}
            </div>

            {activeTab === 'logins' && canViewLogins && <LoginLogsTab users={users} />}
            {activeTab === 'devices' && currentUser?.role === 'admin' && <DevicesTab />}

            {activeTab === 'users' && (<>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                        placeholder="Rechercher par nom ou identifiant..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                        data-testid="search-users"
                    />
                </div>
                <Select value={filterRole} onValueChange={setFilterRole}>
                    <SelectTrigger className="w-full sm:w-48" data-testid="filter-role">
                        <SelectValue placeholder="Tous les rôles" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tous les rôles</SelectItem>
                        {ROLES.map(role => (
                            <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Role Legend */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {ROLES.map(role => {
                    const Icon = role.icon;
                    const count = users.filter(u => (u.role || 'admin') === role.value).length;
                    return (
                        <div 
                            key={role.value}
                            className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200"
                        >
                            <div className={`p-2 rounded-lg ${role.color}`}>
                                <Icon className="w-4 h-4" />
                            </div>
                            <div>
                                <p className="font-medium text-sm text-slate-900">{role.label}</p>
                                <p className="text-xs text-slate-500">{count} utilisateur{count > 1 ? 's' : ''}</p>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Users List */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
                </div>
            ) : filteredUsers.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
                    <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Aucun utilisateur trouvé</p>
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                    {/* Desktop table */}
                    <table className="w-full hidden lg:table">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="text-left p-4 font-semibold text-slate-600">Utilisateur</th>
                                <th className="text-left p-4 font-semibold text-slate-600">Rôle</th>
                                <th className="text-left p-4 font-semibold text-slate-600">Sites</th>
                                <th className="text-left p-4 font-semibold text-slate-600">Guide</th>
                                <th className="text-left p-4 font-semibold text-slate-600">Statut</th>
                                <th className="text-left p-4 font-semibold text-slate-600">Créé le</th>
                                <th className="text-right p-4 font-semibold text-slate-600">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map((user) => {
                                const roleInfo = getRoleInfo(user.role || 'admin');
                                const RoleIcon = roleInfo.icon;
                                const isCurrentUser = currentUser?.id === user.id;
                                const siteNames = getSiteNames(user.allowed_sites);
                                
                                return (
                                    <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="p-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center">
                                                    <span className="text-white font-semibold">
                                                        {user.name?.charAt(0).toUpperCase() || 'U'}
                                                    </span>
                                                </div>
                                                <div>
                                                    <p className="font-medium text-slate-900">
                                                        {user.name}
                                                        {isCurrentUser && (
                                                            <span className="ml-2 text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                                                                Vous
                                                            </span>
                                                        )}
                                                    </p>
                                                    <p className="text-sm text-slate-500">{user.username}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-1">
                                                <Badge className={`${roleInfo.color} border-0`}>
                                                    <RoleIcon className="w-3 h-3 mr-1" />
                                                    {roleInfo.label}
                                                </Badge>
                                                {user.guide_only && (
                                                    <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">
                                                        <BookOpen className="w-3 h-3 mr-0.5" />Guide
                                                    </Badge>
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            {siteNames ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {siteNames.slice(0, 2).map((name, idx) => (
                                                        <Badge key={idx} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                                            <MapPin className="w-3 h-3 mr-1" />
                                                            {name}
                                                        </Badge>
                                                    ))}
                                                    {siteNames.length > 2 && (
                                                        <Badge variant="outline" className="text-xs">
                                                            +{siteNames.length - 2}
                                                        </Badge>
                                                    )}
                                                </div>
                                            ) : (
                                                <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                                                    <Building2 className="w-3 h-3 mr-1" />
                                                    Tous les sites
                                                </Badge>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            {user.guide_sites && user.guide_sites.length > 0 ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {user.guide_sites.map(sid => {
                                                        const s = sites.find(x => x.id === sid);
                                                        return s ? (
                                                            <Badge key={sid} variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                                                                <MapPin className="w-3 h-3 mr-1" />{s.name}
                                                            </Badge>
                                                        ) : null;
                                                    })}
                                                </div>
                                            ) : (
                                                <span className="text-xs text-slate-400">Aucun</span>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            {user.is_active !== false ? (
                                                <Badge className="bg-emerald-100 text-emerald-700 border-0">
                                                    <UserCheck className="w-3 h-3 mr-1" />
                                                    Actif
                                                </Badge>
                                            ) : (
                                                <Badge className="bg-red-100 text-red-700 border-0">
                                                    <UserX className="w-3 h-3 mr-1" />
                                                    Désactivé
                                                </Badge>
                                            )}
                                        </td>
                                        <td className="p-4 text-slate-600">
                                            {formatDate(user.created_at)}
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleEditUser(user)}
                                                    title="Modifier"
                                                    data-testid={`edit-user-${user.id}`}
                                                >
                                                    <Pencil className="w-4 h-4 text-slate-600" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => {
                                                        setSelectedUser(user);
                                                        setPasswordDialog(true);
                                                    }}
                                                    title="Réinitialiser le mot de passe"
                                                    data-testid={`reset-password-${user.id}`}
                                                >
                                                    <KeyRound className="w-4 h-4 text-slate-600" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleToggleActive(user)}
                                                    title={user.is_active !== false ? 'Désactiver' : 'Activer'}
                                                    disabled={isCurrentUser}
                                                    data-testid={`toggle-active-${user.id}`}
                                                >
                                                    {user.is_active !== false ? (
                                                        <UserX className="w-4 h-4 text-amber-600" />
                                                    ) : (
                                                        <UserCheck className="w-4 h-4 text-emerald-600" />
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => {
                                                        setSelectedUser(user);
                                                        setDeleteDialog(true);
                                                    }}
                                                    title="Supprimer"
                                                    disabled={isCurrentUser}
                                                    data-testid={`delete-user-${user.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4 text-red-500" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {/* Mobile cards */}
                    <div className="lg:hidden divide-y divide-slate-100">
                        {filteredUsers.map((user) => {
                            const roleInfo = getRoleInfo(user.role || 'admin');
                            const RoleIcon = roleInfo.icon;
                            const isCurrentUser = currentUser?.id === user.id;
                            return (
                                <div key={user.id} className="p-4" data-testid={`user-card-${user.id}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 bg-slate-900 rounded-full flex items-center justify-center">
                                                <span className="text-white font-semibold text-sm">{user.name?.charAt(0).toUpperCase() || 'U'}</span>
                                            </div>
                                            <div>
                                                <p className="font-medium text-slate-900 text-sm">
                                                    {user.name}
                                                    {isCurrentUser && <span className="ml-1 text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">Vous</span>}
                                                </p>
                                                <p className="text-xs text-slate-500">{user.username}</p>
                                            </div>
                                        </div>
                                        <Badge className={`${roleInfo.color} border-0 text-xs`}>
                                            <RoleIcon className="w-3 h-3 mr-1" />{roleInfo.label}
                                        </Badge>
                                        {user.guide_only && (
                                            <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">
                                                <BookOpen className="w-3 h-3 mr-0.5" />Guide
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-1">
                                            {user.is_active !== false ? (
                                                <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">Actif</Badge>
                                            ) : (
                                                <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">Désactivé</Badge>
                                            )}
                                            {user.guide_sites && user.guide_sites.length > 0 && (
                                                <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">
                                                    Guide: {user.guide_sites.length} site{user.guide_sites.length > 1 ? 's' : ''}
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditUser(user)} data-testid={`edit-user-${user.id}`}>
                                                <Pencil className="w-3.5 h-3.5 text-slate-600" />
                                            </Button>
                                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelectedUser(user); setPasswordDialog(true); }} data-testid={`reset-password-${user.id}`}>
                                                <KeyRound className="w-3.5 h-3.5 text-slate-600" />
                                            </Button>
                                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleToggleActive(user)} disabled={isCurrentUser} data-testid={`toggle-active-${user.id}`}>
                                                {user.is_active !== false ? <UserX className="w-3.5 h-3.5 text-amber-600" /> : <UserCheck className="w-3.5 h-3.5 text-emerald-600" />}
                                            </Button>
                                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelectedUser(user); setDeleteDialog(true); }} disabled={isCurrentUser} data-testid={`delete-user-${user.id}`}>
                                                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            </>)}

            {/* Onglet Profils & Droits */}
            {activeTab === 'profiles' && (
                <RolePermissionsPage />
            )}

            {/* Create User Dialog */}
            <Dialog open={createDialog} onOpenChange={setCreateDialog}>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Nouvel utilisateur</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Nom complet *</Label>
                            <Input
                                value={userForm.name}
                                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                                placeholder="Jean Dupont"
                                data-testid="input-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Identifiant *</Label>
                            <Input
                                type="text"
                                value={userForm.username}
                                onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                                placeholder="jdupont"
                                data-testid="input-username"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Adresse email <span className="text-xs text-slate-400 font-normal">(utilisée pour Reply-To des commandes)</span></Label>
                            <Input
                                type="email"
                                value={userForm.email}
                                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                                placeholder="prenom.nom@entreprise.com"
                                data-testid="input-email"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Mot de passe *</Label>
                            <Input
                                type="password"
                                value={userForm.password}
                                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                                placeholder="••••••••"
                                data-testid="input-password"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Rôle</Label>
                            <Select 
                                value={userForm.role} 
                                onValueChange={(value) => setUserForm({ ...userForm, role: value })}
                            >
                                <SelectTrigger data-testid="select-role">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {ROLES.map(role => (
                                        <SelectItem key={role.value} value={role.value}>
                                            <div className="flex items-center gap-2">
                                                <role.icon className="w-4 h-4" />
                                                <span>{role.label}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-500">
                                {ROLES.find(r => r.value === userForm.role)?.description}
                            </p>
                        </div>
                        
                        <div
                            className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors"
                            onClick={() => setUserForm(prev => ({ ...prev, guide_only: !prev.guide_only }))}
                        >
                            <Checkbox checked={userForm.guide_only} className="pointer-events-none" />
                            <BookOpen className="w-4 h-4 text-red-500" />
                            <div>
                                <p className="text-sm font-medium">Guide uniquement</p>
                                <p className="text-xs text-slate-500">Cet utilisateur n'aura acces qu'au Guide du Sushiman</p>
                            </div>
                        </div>

                        {!userForm.guide_only && <SiteSelector />}
                        <GuideSiteSelector />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateDialog(false)}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleCreateUser} 
                            data-testid="confirm-create-user"
                            disabled={!userForm.allSitesAccess && userForm.allowed_sites.length === 0}
                        >
                            Créer l'utilisateur
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit User Dialog */}
            <Dialog open={editDialog} onOpenChange={setEditDialog}>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Modifier l'utilisateur</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Nom complet</Label>
                            <Input
                                value={userForm.name}
                                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                                data-testid="edit-input-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Identifiant</Label>
                            <Input
                                type="text"
                                value={userForm.username}
                                onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                                data-testid="edit-input-username"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Adresse email <span className="text-xs text-slate-400 font-normal">(utilisée pour Reply-To des commandes)</span></Label>
                            <Input
                                type="email"
                                value={userForm.email}
                                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                                placeholder="prenom.nom@entreprise.com"
                                data-testid="edit-input-email"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Rôle</Label>
                            <Select 
                                value={userForm.role} 
                                onValueChange={(value) => setUserForm({ ...userForm, role: value })}
                            >
                                <SelectTrigger data-testid="edit-select-role">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {ROLES.map(role => (
                                        <SelectItem key={role.value} value={role.value}>
                                            <div className="flex items-center gap-2">
                                                <role.icon className="w-4 h-4" />
                                                <span>{role.label}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-500">
                                {ROLES.find(r => r.value === userForm.role)?.description}
                            </p>
                        </div>
                        
                        <div
                            className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors"
                            onClick={() => setUserForm(prev => ({ ...prev, guide_only: !prev.guide_only }))}
                        >
                            <Checkbox checked={userForm.guide_only} className="pointer-events-none" />
                            <BookOpen className="w-4 h-4 text-red-500" />
                            <div>
                                <p className="text-sm font-medium">Guide uniquement</p>
                                <p className="text-xs text-slate-500">Cet utilisateur n'aura acces qu'au Guide du Sushiman</p>
                            </div>
                        </div>

                        {!userForm.guide_only && <SiteSelector />}
                        <GuideSiteSelector />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditDialog(false)}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleUpdateUser} 
                            data-testid="confirm-edit-user"
                            disabled={!userForm.allSitesAccess && userForm.allowed_sites.length === 0}
                        >
                            Enregistrer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog open={passwordDialog} onOpenChange={setPasswordDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <p className="text-sm text-slate-600">
                            Réinitialisation du mot de passe pour <strong>{selectedUser?.name}</strong>
                        </p>
                        <div className="space-y-2">
                            <Label>Nouveau mot de passe</Label>
                            <div className="relative">
                                <Input
                                    type={showResetPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="pr-10"
                                    data-testid="input-new-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowResetPassword(v => !v)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                                    title={showResetPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                                    data-testid="toggle-reset-password-visibility"
                                >
                                    {showResetPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                            <p className="text-xs text-slate-500">Minimum 8 caractères</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => {
                            setPasswordDialog(false);
                            setNewPassword('');
                            setShowResetPassword(false);
                        }}>
                            Annuler
                        </Button>
                        <Button onClick={handleResetPassword} data-testid="confirm-reset-password">
                            Réinitialiser
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer l'utilisateur ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Êtes-vous sûr de vouloir supprimer <strong>{selectedUser?.name}</strong> ?
                            Cette action est irréversible.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleDeleteUser}
                            className="bg-red-500 hover:bg-red-600"
                            data-testid="confirm-delete-user"
                        >
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
